/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, Chip, styled, Typography } from "@mui/material";
import dayjs from "dayjs";
import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Card, { CardAction, CardTitle } from "../../components/Card";
import { TestIds } from "../../testIds";
import { ScrollYPositionKeys } from "../../util/consts";
import { saveScrollYPosition } from "../../util/helpers";

import PercentageBar from "./components/PercentageBar";
import { formatAgreementDates } from "./utils/formatAgreementDates";
import { getTimeBadge } from "./utils/getTimeBadge";

interface Props {
  id: string;
  title: string;
  startDate: Date;
  endDate: Date;
  percentageFulfilled: number;
  products: string[];
}

const ProductsView = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SupportText = styled(Typography)`
  font-size: 18px;
  line-height: 150%;

  & span {
    margin-left: 4px;
    color: ${({ theme }) => theme.colors.primary500};
  }
`;

export const Inline = styled("div")`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const SalesAgreementListItem = (props: Props) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isPastItem = useMemo(() => {
    return dayjs(props.endDate).isBefore(new Date());
  }, [props.endDate]);

  const agreementTitle = props.title ? props.title : props.id;

  const agreementDetailsNavigate = (
    e: React.MouseEvent<HTMLElement, MouseEvent> | null,
    path: string,
    id: string
  ) => {
    e?.preventDefault();
    navigate(`${path}${id}`);
  };

  return (
    <li>
      <Card
        variant="outlined"
        data-testid={TestIds.feat.agreement.card.agreementCard}
        data-id={props.id}
      >
        <CardAction
          tabIndex={-1}
          disableRipple
          onClick={(e) => {
            const sessionStorageKey = ScrollYPositionKeys.Agreements;
            if (pathname === "/agreements") {
              saveScrollYPosition({ sessionStorageKey });
            } else {
              saveScrollYPosition({ sessionStorageKey, position: 0 });
            }

            agreementDetailsNavigate(e, "/agreement/", props.id);
          }}
        >
          <CardTitle title={agreementTitle} testId={TestIds.feat.agreement.card.name} />
          <Inline>
            <SupportText
              variant="body1"
              data-testid={TestIds.feat.agreement.card.date}
              data-id={props.id}
            >
              {formatAgreementDates(props.startDate, props.endDate)}
            </SupportText>
            {!isPastItem && (
              <Chip
                data-testid={TestIds.feat.agreement.card.timeBadge}
                data-id={props.id}
                color="info"
                label={getTimeBadge(props.startDate, props.endDate)}
              />
            )}
          </Inline>

          <PercentageBar
            data-testid={TestIds.feat.agreement.card.fulfillmentBar}
            data-id={props.id}
            percentage={props.percentageFulfilled}
            gradient="primary"
          />
          <ProductsView>
            {props.products.slice(0, 2).map((e, index) => {
              if (props.products.length > 2 && index === 1) {
                return (
                  <SupportText
                    data-testid={TestIds.feat.agreement.card.product}
                    data-id={props.id}
                    key={`${e}_${index}`}
                    variant="body1"
                  >
                    {e} <span>+ More Products</span>
                  </SupportText>
                );
              }
              return (
                <SupportText
                  data-testid={TestIds.feat.agreement.card.product}
                  data-id={props.id}
                  key={`${e}_${index}`}
                  variant="body1"
                >
                  {e}
                </SupportText>
              );
            })}
          </ProductsView>
        </CardAction>
      </Card>
    </li>
  );
};

export default SalesAgreementListItem;
